import type { ICountry } from 'rn-international-phone-number';
import { getCountryByPhoneNumber } from 'rn-international-phone-number';
import type { CountryCode } from 'react-native-country-picker-modal';

export type DerivedPhoneInput = {
  country: ICountry | undefined;
  callingCode: string;
  nationalNumber: string;
};

export const PREFERRED_COUNTRIES: CountryCode[] = [
  'KE', // Kenya
  'TZ', // Tanzania
  'ET', // Ethiopia
  // 'NG', // Nigeria
  'UG', // Uganda
  'ZA', // South Africa
  'IN', // India
  'US', // United States
];

const stripToDigits = (value: string): string => value.replace(/[^0-9]/g, '');

export function derivePhoneInputFromValue(raw: string): DerivedPhoneInput {
  const value = String(raw || '').trim();
  if (!value) {
    return { country: undefined, callingCode: '', nationalNumber: '' };
  }

  const country = getCountryByPhoneNumber(value);

  // Build a list of possible calling codes for the detected country
  const possibleCodes: string[] = [];
  const addCode = (code: string | undefined) => {
    if (!code) return;
    const normalized = `+${String(code).replace(/^\+/, '')}`.replace(
      /\s+/g,
      '',
    );
    if (!possibleCodes.includes(normalized)) possibleCodes.push(normalized);
  };

  // Prefer library-provided callingCode if available
  // (rn-international-phone-number exposes callingCode on ICountry)
  // Fallback to idd root+suffix combinations like "+213" from root "+2" and suffixes ["13"].
  addCode((country as any)?.callingCode);
  const root = (country as any)?.idd?.root as string | undefined;
  const suffixes =
    ((country as any)?.idd?.suffixes as string[] | undefined) || [];
  if (root) {
    if (suffixes.length > 0) {
      suffixes.forEach(suf => addCode(`${root}${suf}`));
    } else {
      addCode(root);
    }
  }

  let nationalPart = value;
  // Strip the first matching calling code from the start
  for (const code of possibleCodes) {
    const codePattern = new RegExp(`^\\s*\\${code}\\s*`);
    if (codePattern.test(nationalPart)) {
      nationalPart = nationalPart.replace(codePattern, '');
      break;
    }
  }

  // Normalize to digits only for the input field
  nationalPart = stripToDigits(nationalPart);

  return {
    country,
    callingCode: possibleCodes[0] || '',
    nationalNumber: nationalPart,
  };
}

/**
 * Build a sanitized E.164-like string from the national input and selected country.
 * - Removes all non-digits from the national input
 * - Prefixes with the country's calling code when available
 * - Falls back to '+' + digits when country is unavailable
 */
export function buildE164FromInput(
  nationalInput: string,
  country?: ICountry | null,
): string {
  const digitsOnly = stripToDigits(String(nationalInput || ''));
  if (!digitsOnly) return '';

  // Prefer idd root + first suffix when available, else callingCode
  let code = '';
  if (country) {
    const root = (country as any)?.idd?.root as string | undefined;
    const suffixes = (((country as any)?.idd?.suffixes as string[]) ||
      []) as string[];
    if (root) {
      code = `${root}${suffixes?.[0] ?? ''}`.replace(/\s+/g, '');
      if (!code.startsWith('+')) code = `+${code}`;
    } else if ((country as any)?.callingCode) {
      code = `+${String((country as any)?.callingCode).replace(/^\+/, '')}`;
    }
  }

  return `${code || '+'}${digitsOnly}`;
}

/**
 * Normalize an arbitrary phone-like string to E.164-compatible:
 * - Keep only '+' and digits
 * - Ensure it starts with '+'
 */
export function normalizeE164(raw: string): string {
  const v = String(raw || '').trim();
  if (!v) return '';
  let cleaned = v.replace(/[^0-9+]/g, '');
  if (!cleaned.startsWith('+')) {
    cleaned = `+${cleaned.replace(/^\+/, '')}`;
  }
  return cleaned;
}
